/**
 * RegistrationForm Component
 * 
 * Form component for registering a single helper with:
 * - First name (required)
 * - Surname (required)
 * - Email (required - part of unique identifier)
 * - Fachbereiche (optional, multiple selection via checkboxes)
 * 
 * @param onSuccess - Callback function called when registration succeeds
 *                    Receives firstName and surname as parameters
 */
import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import { useFachbereiche } from '../contexts/FachbereicheContext';
import { useAuth } from '../contexts/AuthContext';
import './RegistrationForm.css';
import './Switch.css';

interface HelperFeedback {
  fachbereich: string;
  rückmeldung: string | null;
  bemerkung?: string | null;
}

interface Helper {
  id?: number;
  first_name: string;
  surname: string;
  email: string;
  phone?: string;
  wunscheinsatzort?: string;
  einsatzzeit?: string;
  bereit_fuer_mehrere_einsaetze?: string | boolean; // Backend returns boolean, frontend uses 'Ja'/'Nein'
  hilft_nicht_am_wettkampftag?: number; // 0 = helps on race day, 1 = doesn't help
  anmerkung?: string;
  aufbauhilfe?: string;
  kuchenspende?: number;
  tshirt?: string;
  fachbereiche?: string[];
  feedback?: HelperFeedback[];
}

interface RegistrationFormProps {
  onSuccess: (firstName: string, surname: string) => void;
  onFormChange?: (hasChanges: boolean) => void;
  initialHelper?: Helper | null; // For edit mode
  onCancel?: () => void; // For edit mode - cancel button callback
  onDelete?: () => void; // For edit mode - delete button callback
  helperAssignments?: { [key: number]: any[] }; // For edit mode - to check for assignment warnings
  onBeforeSubmit?: (formData: {
    firstName: string;
    surname: string;
    email: string;
    phone: string;
    selectedFachbereiche: string[];
    wunscheinsatzort: string;
    einsatzzeit: string;
    bereitFuerMehrereEinsaetze: string;
    hilftNichtAmWettkampftag: boolean;
    anmerkung: string;
    aufbauhilfe: string;
    kuchenspende: number;
    tshirt: string;
  }) => Promise<boolean>; // Return false to prevent submission, true to proceed
  yearMaintenanceDisabled?: boolean; // If true, form is read-only
}

const RegistrationForm = forwardRef<HTMLFormElement, RegistrationFormProps>(({ onSuccess, onFormChange, initialHelper = null, onCancel, onDelete, helperAssignments = {}, onBeforeSubmit, yearMaintenanceDisabled = false }, ref) => {
  const formRef = useRef<HTMLFormElement>(null);
  
  // Expose form ref to parent component
  useImperativeHandle(ref, () => formRef.current!);
  const { fachbereiche } = useFachbereiche();
  const { user, isAdmin, token } = useAuth();
  
  // Check if user is Fachbereich Administrator
  const isFachbereichAdmin = user?.role === 'Fachbereich Administrator';
  
  // For Fachbereich Administrators, filter to only their assigned Fachbereiche
  const availableFachbereiche = isAdmin() 
    ? fachbereiche 
    : (user?.fachbereiche && user.fachbereiche.length > 0 
        ? fachbereiche.filter(fb => user.fachbereiche!.includes(fb))
        : []);
  const isEditMode = !!initialHelper;
  
  const [firstName, setFirstName] = useState(initialHelper?.first_name || '');
  const [surname, setSurname] = useState(initialHelper?.surname || '');
  const [email, setEmail] = useState(initialHelper?.email || '');
  const [phone, setPhone] = useState(initialHelper?.phone || '');
  const [selectedFachbereiche, setSelectedFachbereiche] = useState<string[]>(initialHelper?.fachbereiche || []);
  
  // Track initial values to detect actual changes (not just defaults)
  const [initialValues, setInitialValues] = useState<{
    firstName: string;
    surname: string;
    email: string;
    phone: string;
    selectedFachbereiche: string[];
            wunscheinsatzort: string;
            einsatzzeit: string;
            bereitFuerMehrereEinsaetze: string;
            hilftNichtAmWettkampftag: boolean;
            anmerkung: string;
            aufbauhilfe: string;
            kuchenspende: number;
            tshirt: string;
  } | null>(null);
  const hasInitializedRef = useRef(false);

  // For Fachbereich Administrators: if only one Fachbereich available, select it by default (only in registration mode)
  useEffect(() => {
    if (!isEditMode && isFachbereichAdmin && availableFachbereiche.length === 1 && selectedFachbereiche.length === 0) {
      setSelectedFachbereiche([availableFachbereiche[0]]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedFachbereiche.length intentionally excluded
  }, [isEditMode, isFachbereichAdmin, availableFachbereiche]);

  // Set initial values once after Fachbereiche are loaded and auto-selection completes (if applicable)
  // We use a ref to capture the current selectedFachbereiche value to avoid dependency issues
  const selectedFachbereicheRef = useRef(selectedFachbereiche);
  useEffect(() => {
    selectedFachbereicheRef.current = selectedFachbereiche;
  }, [selectedFachbereiche]);

  // Sync identity fields when initialHelper changes (e.g. after identity change in modal)
  useEffect(() => {
    if (isEditMode && initialHelper) {
      setFirstName(initialHelper.first_name || '');
      setSurname(initialHelper.surname || '');
      setEmail(initialHelper.email || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync when identity fields change
  }, [isEditMode, initialHelper?.id, initialHelper?.first_name, initialHelper?.surname, initialHelper?.email]);

  useEffect(() => {
    if (!hasInitializedRef.current) {
      // Use setTimeout to ensure this runs after the Fachbereich auto-selection effect
      const timer = setTimeout(() => {
        if (!hasInitializedRef.current) {
          setInitialValues({
            firstName: isEditMode ? (initialHelper?.first_name || '') : '',
            surname: isEditMode ? (initialHelper?.surname || '') : '',
            email: isEditMode ? (initialHelper?.email || '') : '',
            phone: isEditMode ? (initialHelper?.phone || '') : '',
            selectedFachbereiche: [...selectedFachbereicheRef.current],
            wunscheinsatzort: isEditMode ? (initialHelper?.wunscheinsatzort || '') : '',
            einsatzzeit: isEditMode ? (initialHelper?.einsatzzeit || '') : '',
            bereitFuerMehrereEinsaetze: (() => {
              if (!isEditMode || !initialHelper || initialHelper.bereit_fuer_mehrere_einsaetze === undefined || initialHelper.bereit_fuer_mehrere_einsaetze === null) {
                return 'Nein';
              }
              const bereitValue = initialHelper.bereit_fuer_mehrere_einsaetze;
              // Type-safe conversion: handle boolean, string, or number
              if (bereitValue === true || bereitValue === 'Ja' || bereitValue === '1') {
                return 'Ja';
              }
              // Handle number case separately to avoid TypeScript error
              if (typeof bereitValue === 'number' && bereitValue === 1) {
                return 'Ja';
              }
              return 'Nein';
            })(),
            // Simple logic: Switch ON when value is 1, OFF when value is 0 or empty
            hilftNichtAmWettkampftag: isEditMode && initialHelper && initialHelper.hilft_nicht_am_wettkampftag !== undefined && initialHelper.hilft_nicht_am_wettkampftag !== null
              ? (initialHelper.hilft_nicht_am_wettkampftag === 1) // Read from database: 1 = ON, 0 = OFF
              : false, // Default to OFF for new registration
            anmerkung: isEditMode ? (initialHelper?.anmerkung || '') : '',
            aufbauhilfe: isEditMode ? (initialHelper?.aufbauhilfe || '') : '',
            kuchenspende: isEditMode ? (initialHelper?.kuchenspende !== null && initialHelper?.kuchenspende !== undefined ? initialHelper.kuchenspende : 0) : 0,
            tshirt: isEditMode ? (initialHelper?.tshirt || '') : ''
          });
          hasInitializedRef.current = true;
        }
      }, 10); // Small delay to ensure auto-selection completes
      return () => clearTimeout(timer);
    }
  }, [availableFachbereiche.length, isFachbereichAdmin, isEditMode, initialHelper]);
  
  const [wunscheinsatzort, setWunscheinsatzort] = useState<string>(initialHelper?.wunscheinsatzort || '');
  const [einsatzzeit, setEinsatzzeit] = useState<string>(initialHelper?.einsatzzeit || '');
  // Convert backend boolean/string to frontend 'Ja'/'Nein' string
  // Backend returns: boolean true/false, or string 'Ja'/'Nein', or null/undefined
  // Frontend uses: 'Ja' or 'Nein'
  const [bereitFuerMehrereEinsaetze, setBereitFuerMehrereEinsaetze] = useState<string>(() => {
    if (isEditMode && initialHelper && initialHelper.bereit_fuer_mehrere_einsaetze !== undefined && initialHelper.bereit_fuer_mehrere_einsaetze !== null) {
      const value = initialHelper.bereit_fuer_mehrere_einsaetze;
      // Convert boolean true/1/'Ja'/'1' to 'Ja', everything else to 'Nein'
      // Type-safe conversion: handle boolean, string, or number
      if (value === true || value === 'Ja' || value === '1') {
        return 'Ja';
      }
      // Handle number case separately to avoid TypeScript error
      if (typeof value === 'number' && value === 1) {
        return 'Ja';
      }
      return 'Nein';
    }
    // Creation mode: default to 'Nein'
    return 'Nein';
  });
  // Simple logic: Switch ON when value is 1, OFF when value is 0 or empty
  // In edit mode: read from database (0 = OFF, 1 = ON)
  // In creation mode: default to OFF (false)
  const [hilftNichtAmWettkampftag, setHilftNichtAmWettkampftag] = useState<boolean>(() => {
    if (isEditMode && initialHelper && initialHelper.hilft_nicht_am_wettkampftag !== undefined && initialHelper.hilft_nicht_am_wettkampftag !== null) {
      const value = initialHelper.hilft_nicht_am_wettkampftag;
      // Switch ON only if value is exactly 1
      const isOn = value === 1;
      return isOn;
    }
    // Creation mode: default to OFF
    return false;
  });
  const [anmerkung, setAnmerkung] = useState(initialHelper?.anmerkung || '');
  const [aufbauhilfe, setAufbauhilfe] = useState(initialHelper?.aufbauhilfe || '');
  const [kuchenspende, setKuchenspende] = useState<number>(initialHelper?.kuchenspende !== null && initialHelper?.kuchenspende !== undefined ? initialHelper.kuchenspende : 0);
  const [tshirt, setTshirt] = useState<string>(initialHelper?.tshirt || '');

  /* Textarea auto-resize: height grows with content, responds to dialog width changes */
  const LONG_TEXT_IDS = ['wunscheinsatzort', 'einsatzzeit', 'anmerkung', 'aufbauhilfe'];
  const onTextareaInput = useCallback((e: React.FormEvent<HTMLTextAreaElement>) => {
    const ta = e.currentTarget;
    ta.style.overflow = 'hidden';
    ta.style.height = '0';
    ta.style.height = Math.max(ta.scrollHeight, 72) + 'px';
    ta.style.overflow = '';
  }, []);
  const resizeTextareas = useCallback(() => {
    LONG_TEXT_IDS.forEach(id => {
      const el = document.getElementById(id) as HTMLTextAreaElement | null;
      if (!el) return;
      const minH = 72;
      el.style.overflow = 'hidden';
      el.style.height = '0';
      el.style.height = Math.max(el.scrollHeight, minH) + 'px';
      el.style.overflow = '';
    });
  }, []);
  useEffect(() => {
    resizeTextareas();
  }, [wunscheinsatzort, einsatzzeit, anmerkung, aufbauhilfe, resizeTextareas]);
  useEffect(() => {
    const form = formRef.current;
    if (!form) return;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(resizeTextareas);
    });
    ro.observe(form);
    return () => ro.disconnect();
  }, [resizeTextareas]);

  // Sync state when initialHelper changes (e.g., when editing a different helper)
  // CRITICAL: In edit mode, ALWAYS read from database, NEVER default
  useEffect(() => {
    if (isEditMode && initialHelper) {
      setWunscheinsatzort(initialHelper.wunscheinsatzort || '');
      setEinsatzzeit(initialHelper.einsatzzeit || '');
      // Convert backend boolean/string to frontend 'Ja'/'Nein' string
      // Backend returns: boolean true/false, or string 'Ja'/'Nein', or null/undefined
      const bereitValue = initialHelper.bereit_fuer_mehrere_einsaetze;
      if (bereitValue !== undefined && bereitValue !== null) {
        // Convert boolean true/1/'Ja'/'1' to 'Ja', everything else to 'Nein'
        // Type-safe conversion: handle boolean, string, or number
        if (bereitValue === true || bereitValue === 'Ja' || bereitValue === '1') {
          setBereitFuerMehrereEinsaetze('Ja');
        } else if (typeof bereitValue === 'number' && bereitValue === 1) {
          setBereitFuerMehrereEinsaetze('Ja');
        } else {
          setBereitFuerMehrereEinsaetze('Nein');
        }
      } else {
        setBereitFuerMehrereEinsaetze('Nein');
      }
      // Simple logic: Switch ON when value is 1, OFF when value is 0 or empty
      const dbValue = initialHelper.hilft_nicht_am_wettkampftag;
      // Switch ON only if value is exactly 1
      const switchOn = dbValue === 1;
      setHilftNichtAmWettkampftag(switchOn);
      setAnmerkung(initialHelper.anmerkung || '');
      setAufbauhilfe(initialHelper.aufbauhilfe || '');
      setKuchenspende(initialHelper.kuchenspende !== null && initialHelper.kuchenspende !== undefined ? initialHelper.kuchenspende : 0);
      setTshirt(initialHelper.tshirt || '');
      // Sync Fachbereiche from initialHelper
      if (initialHelper.fachbereiche && Array.isArray(initialHelper.fachbereiche)) {
        setSelectedFachbereiche([...initialHelper.fachbereiche]);
      } else {
        setSelectedFachbereiche([]);
      }
      // Reset initialization flag when helper changes
      hasInitializedRef.current = false;
    } else if (!isEditMode) {
      // In creation mode, ensure it's false
      setHilftNichtAmWettkampftag(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initialHelper dependency intentionally partial
  }, [isEditMode, initialHelper?.id, initialHelper?.hilft_nicht_am_wettkampftag, initialHelper?.fachbereiche]);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const firstNameRef = useRef<HTMLInputElement>(null);
  const surnameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  // Exempt from Fachbereich assignment: doesn't help on race day AND no aufbauhilfe
  // These helpers don't need Fachbereich - assignment is disabled and not required
  const isFachbereichAssignmentExempt = hilftNichtAmWettkampftag && 
    (!aufbauhilfe || aufbauhilfe.trim() === '');

  // Fachbereich is optional when exempt; also when hilft_nicht + no aufbauhilfe + kuchenspende (legacy)
  const isFachbereichOptional = isFachbereichAssignmentExempt || 
    (hilftNichtAmWettkampftag && (!aufbauhilfe || aufbauhilfe.trim() === '') && kuchenspende > 0);

  // Check if form has unsaved changes by comparing against initial values
  const hasUnsavedChanges = initialValues !== null && (
    firstName.trim() !== initialValues.firstName ||
    surname.trim() !== initialValues.surname ||
    email.trim() !== initialValues.email ||
    phone.trim() !== initialValues.phone ||
    JSON.stringify([...selectedFachbereiche].sort()) !== JSON.stringify([...initialValues.selectedFachbereiche].sort()) ||
    wunscheinsatzort.trim() !== initialValues.wunscheinsatzort ||
    einsatzzeit.trim() !== initialValues.einsatzzeit ||
    bereitFuerMehrereEinsaetze !== initialValues.bereitFuerMehrereEinsaetze ||
    hilftNichtAmWettkampftag !== initialValues.hilftNichtAmWettkampftag ||
    anmerkung.trim() !== initialValues.anmerkung ||
    aufbauhilfe.trim() !== initialValues.aufbauhilfe ||
    kuchenspende !== initialValues.kuchenspende ||
    tshirt.trim() !== initialValues.tshirt
  );

  // Notify parent of form change state
  useEffect(() => {
    if (onFormChange) {
      onFormChange(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onFormChange]);

  // Set German validation messages
  useEffect(() => {
    if (firstNameRef.current) {
      firstNameRef.current.setCustomValidity('');
      firstNameRef.current.oninvalid = (e) => {
        e.preventDefault();
        (e.target as HTMLInputElement).setCustomValidity('Bitte fülle dieses Feld aus');
      };
    }
    if (surnameRef.current) {
      surnameRef.current.setCustomValidity('');
      surnameRef.current.oninvalid = (e) => {
        e.preventDefault();
        (e.target as HTMLInputElement).setCustomValidity('Bitte fülle dieses Feld aus');
      };
    }
    if (emailRef.current) {
      emailRef.current.setCustomValidity('');
      emailRef.current.oninvalid = (e) => {
        e.preventDefault();
        const target = e.target as HTMLInputElement;
        if (target.validity.valueMissing) {
          target.setCustomValidity('Bitte füllen Sie dieses Feld aus');
        } else if (target.validity.typeMismatch) {
          target.setCustomValidity('Bitte gib eine gültige E-Mail-Adresse ein');
        }
      };
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Basic client-side validation
    if (!firstName.trim() || !surname.trim() || !email.trim()) {
      setError('Bitte fülle alle Pflichtfelder aus');
      setIsSubmitting(false);
      return;
    }

    // Validate email format (required)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Bitte gib eine gültige E-Mail-Adresse ein');
      setIsSubmitting(false);
      return;
    }

    // Validate mandatory fields - only required if helper helps on race day
    if (!hilftNichtAmWettkampftag) {
      // Helper helps on race day - fields are mandatory
      if (!wunscheinsatzort.trim()) {
        setError('Bitte gib einen Wunscheinsatzort ein');
        setIsSubmitting(false);
        return;
      }

      if (!einsatzzeit.trim()) {
        setError('Bitte gib eine Einsatzzeit ein');
        setIsSubmitting(false);
        return;
      }
    }

    // No validation needed - switch always has a value ('Ja' or 'Nein')

    // Validate Anmerkung length
    if (anmerkung.length > 200) {
      setError('Die Anmerkung darf maximal 200 Zeichen lang sein');
      setIsSubmitting(false);
      return;
    }

    // Validate Aufbauhilfe length
    if (aufbauhilfe.length > 300) {
      setError('Die Aufbauhilfe darf maximal 300 Zeichen lang sein');
      setIsSubmitting(false);
      return;
    }

    // Validate Kuchenspende range
    if (kuchenspende < 0 || kuchenspende > 5) {
      setError('Die Kuchenspende muss zwischen 0 und 5 liegen');
      setIsSubmitting(false);
      return;
    }

    // For Fachbereich Administrators, Fachbereich is mandatory UNLESS it's optional due to conditions
    // Fachbereich becomes optional when: hilft_nicht_am_wettkampftag is true AND aufbauhilfe is empty AND kuchenspende > 0
    if (isFachbereichAdmin && selectedFachbereiche.length === 0 && !isFachbereichOptional) {
      setError('Bitte wählen Sie mindestens einen Fachbereich aus');
      setIsSubmitting(false);
      return;
    }

    // Call onBeforeSubmit if provided (for assignment warning checks, etc.)
    if (onBeforeSubmit) {
      const shouldProceed = await onBeforeSubmit({
        firstName: firstName.trim(),
        surname: surname.trim(),
        email: email.trim(),
        phone: phone.trim(),
        selectedFachbereiche,
        wunscheinsatzort,
        einsatzzeit,
        bereitFuerMehrereEinsaetze,
        hilftNichtAmWettkampftag,
        anmerkung,
        aufbauhilfe,
        kuchenspende,
        tshirt
      });
      
      if (!shouldProceed) {
        setIsSubmitting(false);
        return;
      }
    }

    try {
      if (isEditMode && initialHelper?.id) {
        // Edit mode: use PUT request
        const requestPayload = {
          first_name: firstName.trim(),
          surname: surname.trim(),
          email: email.trim(), // Email is sent but backend won't update it (part of identifier)
          phone: phone.trim() || undefined,
          fachbereiche: selectedFachbereiche,
          wunscheinsatzort: wunscheinsatzort.trim() || null,
          einsatzzeit: einsatzzeit.trim() || null,
          bereit_fuer_mehrere_einsaetze: bereitFuerMehrereEinsaetze,
          hilft_nicht_am_wettkampftag: hilftNichtAmWettkampftag ? 1 : 0,
          anmerkung: anmerkung.trim() || undefined,
          aufbauhilfe: aufbauhilfe.trim() || undefined,
          kuchenspende: kuchenspende,
          tshirt: tshirt || undefined,
        };
        const response = await axios.put(`${API_BASE_URL}/api/helper/${initialHelper.id}`, requestPayload, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.data.success) {
          onSuccess(firstName.trim(), surname.trim());
        } else {
          setError(response.data.message || 'Fehler beim Aktualisieren des Helfers');
        }
      } else {
        // Registration mode: use POST request
        const response = await axios.post(`${API_BASE_URL}/api/register`, {
          firstName: firstName.trim(),
          surname: surname.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          fachbereiche: selectedFachbereiche.length > 0 ? selectedFachbereiche : undefined,
          wunscheinsatzort: wunscheinsatzort.trim() || null,
          einsatzzeit: einsatzzeit.trim() || null,
          bereitFuerMehrereEinsaetze: bereitFuerMehrereEinsaetze,
          hilftNichtAmWettkampftag: hilftNichtAmWettkampftag ? 1 : 0,
          anmerkung: anmerkung.trim() || undefined,
          aufbauhilfe: aufbauhilfe.trim() || undefined,
          kuchenspende: kuchenspende,
          tshirt: tshirt || undefined,
        });

        if (response.data.success) {
          onSuccess(firstName.trim(), surname.trim());
        } else {
          setError(response.data.message || 'Anmeldung fehlgeschlagen');
        }
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError(err.response.data?.message || 'Die Bearbeitung von Daten für dieses Jahr ist deaktiviert.');
      } else {
        setError(
          err.response?.data?.message || 
          'Verbindung zum Server nicht möglich. Bitte stelle sicher, dass der Backend-Server läuft.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="registration-form">
      <div className="form-group">
        <label htmlFor="firstName">Vorname <span className="required-asterisk" aria-label="Pflichtfeld">*</span>
          {isEditMode && <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '8px' }}>(über „Identitätsdaten ändern“ bearbeitbar)</span>}
        </label>
        <input
          ref={firstNameRef}
          type="text"
          id="firstName"
          name="firstName"
          value={firstName}
          onChange={(e) => {
            if (!isEditMode) {
              setFirstName(e.target.value);
              e.target.setCustomValidity('');
              setError(''); // Clear error when user starts typing
            }
          }}
          placeholder="Gib den Vornamen ein"
          disabled={isSubmitting || isEditMode || yearMaintenanceDisabled}
          readOnly={isEditMode || yearMaintenanceDisabled}
          required
          aria-required="true"
          aria-invalid={error && !firstName.trim() ? 'true' : 'false'}
          aria-describedby={error && !firstName.trim() ? 'firstName-error' : undefined}
          className={error && !firstName.trim() ? 'error' : ''}
          style={(isEditMode || yearMaintenanceDisabled) ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
        />
        {error && !firstName.trim() && (
          <span id="firstName-error" className="field-error" role="alert">
            Bitte fülle dieses Feld aus
          </span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="surname">Nachname <span className="required-asterisk" aria-label="Pflichtfeld">*</span>
          {isEditMode && <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '8px' }}>(über „Identitätsdaten ändern“ bearbeitbar)</span>}
        </label>
        <input
          ref={surnameRef}
          type="text"
          id="surname"
          name="surname"
          value={surname}
          onChange={(e) => {
            if (!isEditMode && !yearMaintenanceDisabled) {
              setSurname(e.target.value);
              e.target.setCustomValidity('');
              setError(''); // Clear error when user starts typing
            }
          }}
          placeholder="Gib den Nachnamen ein"
          disabled={isSubmitting || isEditMode || yearMaintenanceDisabled}
          readOnly={isEditMode || yearMaintenanceDisabled}
          required
          aria-required="true"
          aria-invalid={error && !surname.trim() ? 'true' : 'false'}
          aria-describedby={error && !surname.trim() ? 'surname-error' : undefined}
          className={error && !surname.trim() ? 'error' : ''}
          style={(isEditMode || yearMaintenanceDisabled) ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
        />
        {error && !surname.trim() && (
          <span id="surname-error" className="field-error" role="alert">
            Bitte fülle dieses Feld aus
          </span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="email">
          E-Mail <span className="required-asterisk" aria-label="Pflichtfeld">*</span>
          {isEditMode && <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '8px' }}>(über „Identitätsdaten ändern“ bearbeitbar)</span>}
        </label>
        <input
          ref={emailRef}
          type="email"
          id="email"
          name="email"
          value={email}
          onChange={(e) => {
            if (!isEditMode && !yearMaintenanceDisabled) {
              setEmail(e.target.value);
              e.target.setCustomValidity('');
              setError(''); // Clear error when user starts typing
            }
          }}
          placeholder="email@beispiel.de"
          disabled={isSubmitting || isEditMode || yearMaintenanceDisabled}
          readOnly={isEditMode || yearMaintenanceDisabled}
          required
          aria-required="true"
          aria-invalid={error && (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) ? 'true' : 'false'}
          aria-describedby={error && (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) ? 'email-error' : undefined}
          className={error && (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) ? 'error' : ''}
          style={(isEditMode || yearMaintenanceDisabled) ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
        />
        {(error && !email.trim()) && (
          <span id="email-error" className="field-error" role="alert">
            Bitte fülle dieses Feld aus
          </span>
        )}
        {error && email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) && (
          <span id="email-error" className="field-error" role="alert">
            Bitte gib eine gültige E-Mail-Adresse ein
          </span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="phone">Telefonnummer</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={phone}
          onChange={(e) => {
            if (!yearMaintenanceDisabled) {
              setPhone(e.target.value);
            }
          }}
          placeholder="+49 123 456789"
          disabled={isSubmitting || yearMaintenanceDisabled}
          aria-label="Telefonnummer"
        />
      </div>

      <div className="form-row form-row-switches">
        <div className="form-group">
          <label htmlFor="hilftNichtAmWettkampftag">Hilft am Wettkampftag</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
            <span style={{ fontSize: '0.9rem', color: '#666', whiteSpace: 'nowrap' }}>
              {hilftNichtAmWettkampftag ? 'Hilft nicht am Wettkampftag' : 'Hilft am Wettkampftag'}
            </span>
            <label className={`switch-container ${isSubmitting ? 'disabled' : ''}`} style={{ opacity: isSubmitting ? 0.6 : 1 }}>
              <input
                type="checkbox"
                id="hilftNichtAmWettkampftag"
                name="hilftNichtAmWettkampftag"
                className="switch-input"
                checked={hilftNichtAmWettkampftag}
                data-debug-value={initialHelper?.hilft_nicht_am_wettkampftag}
                onChange={(e) => {
                  if (!yearMaintenanceDisabled) {
                    setHilftNichtAmWettkampftag(e.target.checked);
                    setError('');
                  }
                }}
                disabled={isSubmitting || yearMaintenanceDisabled}
              />
              <span className="switch-track switch-track-red">
                <span className="switch-thumb" />
              </span>
            </label>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="bereitFuerMehrereEinsaetze">Bereit für mehrere Einsätze</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
            <span style={{ fontSize: '0.9rem', color: '#666', whiteSpace: 'nowrap' }}>
              {bereitFuerMehrereEinsaetze === 'Ja' ? 'Ja' : 'Nein'}
            </span>
            <label className={`switch-container ${isSubmitting ? 'disabled' : ''}`} style={{ opacity: isSubmitting ? 0.6 : 1 }}>
              <input
                type="checkbox"
                id="bereitFuerMehrereEinsaetze"
                name="bereitFuerMehrereEinsaetze"
                className="switch-input"
                checked={bereitFuerMehrereEinsaetze === 'Ja'}
                onChange={(e) => {
                  if (!yearMaintenanceDisabled) {
                    setBereitFuerMehrereEinsaetze(e.target.checked ? 'Ja' : 'Nein');
                    setError('');
                  }
                }}
                disabled={isSubmitting || yearMaintenanceDisabled}
              />
              <span className="switch-track switch-track-red">
                <span className="switch-thumb" />
              </span>
            </label>
          </div>
        </div>
      </div>

      {!hilftNichtAmWettkampftag && (
        <>
          <div className="form-group">
            <label htmlFor="wunscheinsatzort">Wunscheinsatzort <span className="required-asterisk" aria-label="Pflichtfeld">*</span></label>
            <textarea
              id="wunscheinsatzort"
              name="wunscheinsatzort"
              value={wunscheinsatzort}
              onChange={(e) => {
                if (!yearMaintenanceDisabled && e.target.value.length <= 200) {
                  setWunscheinsatzort(e.target.value);
                  setError('');
                }
              }}
              onInput={onTextareaInput}
              placeholder="z.B. Start/Ziel, Wechselzone 1, etc."
              required
              disabled={isSubmitting || yearMaintenanceDisabled}
              maxLength={200}
              rows={3}
              className={error && !wunscheinsatzort.trim() ? 'error' : ''}
            />
            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '-4px' }}>
              {wunscheinsatzort.length}/200 Zeichen
            </div>
            {error && !wunscheinsatzort.trim() && (
              <span className="field-error" role="alert">
                Bitte gib einen Wunscheinsatzort ein
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="einsatzzeit">Einsatzzeit <span className="required-asterisk" aria-label="Pflichtfeld">*</span></label>
            <textarea
              id="einsatzzeit"
              name="einsatzzeit"
              value={einsatzzeit}
              onChange={(e) => {
                if (!yearMaintenanceDisabled && e.target.value.length <= 100) {
                  setEinsatzzeit(e.target.value);
                  setError('');
                }
              }}
              onInput={onTextareaInput}
              placeholder="z.B. Vormittags, Nachmittags, Abends, etc."
              required
              disabled={isSubmitting || yearMaintenanceDisabled}
              maxLength={100}
              rows={3}
              className={error && !einsatzzeit.trim() ? 'error' : ''}
            />
            <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '-4px' }}>
              {einsatzzeit.length}/100 Zeichen
            </div>
            {error && !einsatzzeit.trim() && (
              <span className="field-error" role="alert">
                Bitte gib eine Einsatzzeit ein
              </span>
            )}
          </div>
        </>
      )}

      <div className="form-group">
        <label htmlFor="anmerkung">Anmerkung (max. 200 Zeichen)</label>
        <textarea
          id="anmerkung"
          name="anmerkung"
          value={anmerkung}
          onChange={(e) => {
            if (!yearMaintenanceDisabled && e.target.value.length <= 200) {
              setAnmerkung(e.target.value);
              setError('');
            }
          }}
          onInput={onTextareaInput}
          placeholder="Optionale Anmerkungen..."
          disabled={isSubmitting || yearMaintenanceDisabled}
          rows={3}
          maxLength={200}
          className={error && anmerkung.length > 200 ? 'error' : ''}
        />
        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '-4px' }}>
          {anmerkung.length}/200 Zeichen
        </div>
        {error && anmerkung.length > 200 && (
          <span className="field-error" role="alert">
            Die Anmerkung darf maximal 200 Zeichen lang sein
          </span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="aufbauhilfe">Aufbauhilfe</label>
        <textarea
          id="aufbauhilfe"
          name="aufbauhilfe"
          value={aufbauhilfe}
          onChange={(e) => {
            if (!yearMaintenanceDisabled && e.target.value.length <= 300) {
              setAufbauhilfe(e.target.value);
              setError('');
            }
          }}
          onInput={onTextareaInput}
          placeholder="Optionale Angaben zur Aufbauhilfe..."
          disabled={isSubmitting || yearMaintenanceDisabled}
          rows={3}
          maxLength={300}
          className={error && aufbauhilfe.length > 300 ? 'error' : ''}
        />
        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '-4px' }}>
          {aufbauhilfe.length}/300 Zeichen
        </div>
        {error && aufbauhilfe.length > 300 && (
          <span className="field-error" role="alert">
            Die Aufbauhilfe darf maximal 300 Zeichen lang sein
          </span>
        )}
      </div>

      <div className="form-row form-row-tshirt-kuchen">
        <div className="form-group">
          <label htmlFor="tshirt">T-Shirt Größe</label>
          <select
            id="tshirt"
            name="tshirt"
            value={tshirt}
            onChange={(e) => {
              if (!yearMaintenanceDisabled) {
                setTshirt(e.target.value);
              }
            }}
            disabled={isSubmitting || yearMaintenanceDisabled}
          >
            <option value="">-- Keine Auswahl --</option>
            <option value="XS">XS</option>
            <option value="S">S</option>
            <option value="M">M</option>
            <option value="L">L</option>
            <option value="XL">XL</option>
            <option value="XXL">XXL</option>
            <option value="XXXL">XXXL</option>
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="kuchenspende">Kuchenspende (0-5)</label>
          <input
            type="number"
            id="kuchenspende"
            name="kuchenspende"
            value={kuchenspende}
            onChange={(e) => {
              if (!yearMaintenanceDisabled) {
                const value = parseInt(e.target.value) || 0;
                if (value >= 0 && value <= 5) {
                  setKuchenspende(value);
                  setError('');
                }
              }
            }}
            min={0}
            max={5}
            disabled={isSubmitting || yearMaintenanceDisabled}
            className={error && (kuchenspende < 0 || kuchenspende > 5) ? 'error' : ''}
          />
          {error && (kuchenspende < 0 || kuchenspende > 5) && (
            <span className="field-error" role="alert">
              Die Kuchenspende muss zwischen 0 und 5 liegen
            </span>
          )}
        </div>
      </div>

      <div className="form-group">
        <label>
          Zuordnung zu Fachbereich
          {isFachbereichAdmin && !isFachbereichOptional ? (
            <span className="required-asterisk" aria-label="Pflichtfeld"> *</span>
          ) : (
            ' (optional - mehrere möglich)'
          )}
        </label>
        {isFachbereichAssignmentExempt && (
          <p style={{ fontSize: '0.85rem', color: '#666', fontStyle: 'italic', marginBottom: '8px' }}>
            Fachbereich-Zuordnung nicht erforderlich (Hilft nicht am Wettkampftag, keine Aufbauhilfe)
          </p>
        )}
        <div className="fachbereiche-checkboxes">
          {availableFachbereiche.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic' }}>
              Keine Fachbereiche verfügbar. Bitte kontaktiere einen Administrator.
            </p>
          ) : (
            availableFachbereiche.map((fb) => {
              const isReadOnly = !isEditMode && isFachbereichAdmin && availableFachbereiche.length === 1;
              const isFachbereichDisabled = isFachbereichAssignmentExempt;
              const isChecked = selectedFachbereiche.includes(fb);
              const wasOriginallyChecked = isEditMode && initialHelper?.fachbereiche?.includes(fb);
              const isBeingRemoved = isEditMode && wasOriginallyChecked && !isChecked;
              const hasAssignments = isEditMode && initialHelper?.id && helperAssignments[initialHelper.id]?.some((a: any) => a.fachbereich === fb);
              const hasRueckmeldung = isEditMode && initialHelper?.feedback?.some((f: { fachbereich: string; rückmeldung?: string | null }) => f.fachbereich === fb && f.rückmeldung && String(f.rückmeldung).trim() !== '');
              const hasWarning = isBeingRemoved && (hasAssignments || hasRueckmeldung);
              
              return (
                <label 
                  key={fb} 
                  className={`checkbox-label ${hasWarning ? 'helper-edit-checkbox-warning' : ''} ${isFachbereichDisabled ? 'disabled' : ''}`}
                  title={hasWarning ? '⚠️ Entfernen dieser Zuordnung löscht auch Positionen-Zuordnungen und/oder Rückmeldungen (inkl. „Passt nicht“)!' : isFachbereichDisabled ? 'Fachbereich-Zuordnung nicht erforderlich' : ''}
                >
                  <input
                    type="checkbox"
                    id={`fachbereich-${fb}`}
                    name={`fachbereich-${fb}`}
                    value={fb}
                    checked={isChecked}
                    onChange={(e) => {
                      if (!isReadOnly && !isFachbereichDisabled && !yearMaintenanceDisabled) {
                        if (e.target.checked) {
                          setSelectedFachbereiche([...selectedFachbereiche, fb]);
                          // Clear error when Fachbereich is selected (for Fachbereich Administrators)
                          if (isFachbereichAdmin && error && error.includes('Fachbereich')) {
                            setError('');
                          }
                        } else {
                          setSelectedFachbereiche(selectedFachbereiche.filter(f => f !== fb));
                        }
                      }
                    }}
                    disabled={isSubmitting || isReadOnly || isFachbereichDisabled || yearMaintenanceDisabled}
                    readOnly={isReadOnly || isFachbereichDisabled || yearMaintenanceDisabled}
                    className="checkbox-input"
                  />
                  <span style={{ 
                    color: hasWarning ? '#ed1c24' : 'inherit',
                    fontWeight: hasWarning ? 'bold' : 'normal'
                  }}>
                    {hasWarning && '⚠️ '}
                    {fb}
                    {hasWarning && (() => {
                      const assignCount = helperAssignments[initialHelper?.id || 0]?.filter((a: any) => a.fachbereich === fb).length || 0;
                      const parts: string[] = [];
                      if (assignCount > 0) parts.push(`${assignCount} Position${assignCount !== 1 ? 'en' : ''} werden entfernt`);
                      if (hasRueckmeldung) parts.push('Rückmeldung geht verloren');
                      return ` (${parts.join(', ')})`;
                    })()}
                  </span>
                </label>
              );
            })
          )}
        </div>
        {isFachbereichAdmin && selectedFachbereiche.length === 0 && !isFachbereichOptional && error && (
          <span className="field-error" role="alert">
            Bitte wählen Sie mindestens einen Fachbereich aus
          </span>
        )}
      </div>

      {yearMaintenanceDisabled && (
        <div className="error-message" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404', marginBottom: '16px', padding: '8px 12px' }}>
          ⚠️ Das Jahr ist schreibgeschützt. Helfer können nicht erstellt, bearbeitet oder gelöscht werden.
        </div>
      )}
      {error && (
        <div className="error-message" role="alert" aria-live="polite">
          {error}
        </div>
      )}

      <div className="registration-form-actions" style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
        {isEditMode && onDelete && (
          <button 
            type="button"
            onClick={onDelete}
            className="helper-list-delete-button"
            style={{ backgroundColor: '#dc3545', color: 'white', flex: '0 0 auto', fontSize: '0.9rem', padding: '6px 12px', opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
            disabled={isSubmitting || yearMaintenanceDisabled}
            title={yearMaintenanceDisabled ? 'Das Jahr ist schreibgeschützt' : "Helfer löschen"}
          >
            🛑 Löschen
          </button>
        )}
        {isEditMode && onCancel && (
          <button 
            type="button"
            onClick={onCancel}
            className="submit-button"
            style={{ backgroundColor: '#666', flex: '0 0 auto' }}
            disabled={isSubmitting}
          >
            Abbrechen
          </button>
        )}
        <button 
          type="submit" 
          className="submit-button"
          disabled={isSubmitting || yearMaintenanceDisabled}
          aria-busy={isSubmitting}
          aria-label={isSubmitting ? (isEditMode ? 'Wird gespeichert...' : 'Wird registriert...') : (isEditMode ? 'Helfer speichern' : 'Helfer registrieren')}
          style={{ flex: '0 0 auto', opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
          title={yearMaintenanceDisabled ? 'Das Jahr ist schreibgeschützt' : ''}
        >
          {isSubmitting ? (isEditMode ? 'Wird gespeichert...' : 'Wird registriert...') : (isEditMode ? 'Speichern' : 'Helfer registrieren')}
        </button>
      </div>
    </form>
  );
});

RegistrationForm.displayName = 'RegistrationForm';

export default RegistrationForm;

